import { AlertOctagon, CheckCircle2, Sparkles, TriangleAlert, Users } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { apiPost } from '../api/client.js';
import { AIBlocks, type AIBlock } from '../components/AIBlocks.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Field, Input, Select } from '../components/Input.js';
import { PageHeader } from '../components/PageHeader.js';
import { Skeleton } from '../components/Skeleton.js';

interface Values {
  product: string;
  minFeedbackPct: number;
  minOrders: number;
  maxShipDays: number;
  returnsPolicy: string;
  platform: string;
  maxCostUsd: number;
}

const SIGNALS: {
  icon: typeof CheckCircle2;
  title: string;
  body: string;
  tone: 'good' | 'warn' | 'bad';
}[] = [
  {
    icon: CheckCircle2,
    title: 'Feedback above 97%',
    body: 'Below 95% = counterfeit or quality risk. Non-negotiable.',
    tone: 'good',
  },
  {
    icon: CheckCircle2,
    title: '1,000+ orders on this product',
    body: 'Under 100 = untested. Avoid for your main store.',
    tone: 'good',
  },
  {
    icon: TriangleAlert,
    title: 'Ships to Canada under 15 days',
    body: 'eBay penalizes slow delivery. Disputes spike after 15 days.',
    tone: 'warn',
  },
  {
    icon: TriangleAlert,
    title: 'Returns accepted, 15-day minimum',
    body: 'No-returns vendor = you absorb every refund personally.',
    tone: 'warn',
  },
  {
    icon: AlertOctagon,
    title: 'Price 50%+ below market = danger',
    body: 'Counterfeit signal. Risk of eBay ban. Walk away.',
    tone: 'bad',
  },
];

const TONE_BG: Record<'good' | 'warn' | 'bad', string> = {
  good: 'var(--c-success-bg)',
  warn: 'var(--c-warn-bg)',
  bad: 'var(--c-danger-bg)',
};
const TONE_FG: Record<'good' | 'warn' | 'bad', string> = {
  good: 'var(--c-success)',
  warn: 'var(--c-warn)',
  bad: 'var(--c-danger)',
};

export function VendorsPage(): JSX.Element {
  const { register, handleSubmit } = useForm<Values>({
    defaultValues: {
      product: 'Wireless earbuds TWS',
      minFeedbackPct: 97,
      minOrders: 500,
      maxShipDays: 15,
      returnsPolicy: '15+ day returns',
      platform: 'All platforms',
      maxCostUsd: 20,
    },
  });
  const [blocks, setBlocks] = useState<AIBlock[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (v: Values): Promise<void> => {
    setLoading(true);
    setError(null);
    setBlocks(null);
    try {
      const { product, ...filters } = v;
      const res = await apiPost<{ blocks: AIBlock[] }>('/api/ai/vendors', { product, filters });
      setBlocks(res.blocks);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Comparison failed.';
      setError(
        msg.includes('No AI provider configured')
          ? 'AI is not configured yet. Open Admin and pick a provider and paste your API key.'
          : msg,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="Vendor"
        accent="comparison"
        sub="Score suppliers against your minimum quality bar before placing the first order."
      />

      <Card title="Vendor filters">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 14,
              marginBottom: 14,
            }}
          >
            <Field label="Min feedback %">
              <Input type="number" {...register('minFeedbackPct', { valueAsNumber: true })} />
            </Field>
            <Field label="Min orders on product">
              <Input type="number" {...register('minOrders', { valueAsNumber: true })} />
            </Field>
            <Field label="Max ship days CA">
              <Input type="number" {...register('maxShipDays', { valueAsNumber: true })} />
            </Field>
            <Field label="Returns policy">
              <Select {...register('returnsPolicy')} aria-label="Returns policy">
                {['Any', 'Returns accepted', '15+ day returns', '30+ day returns'].map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </Select>
            </Field>
            <Field label="Platform">
              <Select {...register('platform')} aria-label="Platform">
                {['All platforms', 'AliExpress', 'Amazon', 'Walmart', 'CJ Dropshipping'].map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </Select>
            </Field>
            <Field label="Max cost USD">
              <Input type="number" {...register('maxCostUsd', { valueAsNumber: true })} />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Input
              {...register('product')}
              placeholder="Product to compare suppliers for"
              style={{ flex: 1 }}
            />
            <Button type="submit" icon={Sparkles} loading={loading}>
              Compare with AI
            </Button>
          </div>
        </form>
      </Card>

      <Card
        title="AI vendor comparison"
        description="Verify scores on the supplier site before ordering — Claude's estimates are based on general market knowledge."
      >
        {loading && <Skeleton height={140} />}
        {error && (
          <div
            style={{
              padding: 12,
              borderRadius: 'var(--radius-md)',
              background: 'var(--c-danger-bg)',
              color: 'var(--c-danger)',
              fontSize: 'var(--text-md)',
            }}
          >
            {error}
          </div>
        )}
        {blocks ? (
          <AIBlocks blocks={blocks} />
        ) : (
          !loading &&
          !error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: 14,
                borderRadius: 'var(--radius-md)',
                background: 'var(--c-divider)',
                color: 'var(--c-muted)',
                fontSize: 'var(--text-md)',
              }}
            >
              <Users size={18} />
              <span>Pick filters and click Compare with AI.</span>
            </div>
          )
        )}
      </Card>

      <Card
        title="Quality signal checklist"
        description="Hold every supplier you contact against these five rules. They aren't suggestions."
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {SIGNALS.map((s) => (
            <div
              key={s.title}
              style={{
                display: 'flex',
                gap: 12,
                padding: 12,
                borderRadius: 'var(--radius-md)',
                background: TONE_BG[s.tone],
              }}
            >
              <s.icon size={20} color={TONE_FG[s.tone]} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 'var(--text-md)', fontWeight: 600, color: TONE_FG[s.tone] }}>
                  {s.title}
                </div>
                <div style={{ fontSize: 'var(--text-base)', color: 'var(--c-text-secondary)', marginTop: 2 }}>
                  {s.body}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
